import {
  AutoStoriesOutlined,
  AutoStoriesRounded,
  PersonOutlineRounded,
  PersonRounded,
  StyleOutlined,
  StyleRounded,
} from "@mui/icons-material";
import classNames from "classnames";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import { auth } from "../../configurations/firebaseConfig";
import { useProfileImageSrc } from "../../features/auth/useProfileImageSrc";
import { useMyRankColor } from "../../features/auth/useMyRankColor";
import styles from "./css/AppShell.module.css";

export const appRoutes = {
  terms: "/",
  learn: "/learn",
} as const;

interface AppShellProps {
  children: ReactNode;
  // 화면 위를 덮는 패널 · 다이얼로그. 레이아웃 흐름에 영향을 주지 않는 위치에 그린다.
  overlays?: ReactNode;
  // 프로필(데스크톱) 또는 내 정보 탭(모바일)을 눌렀을 때. 계정 메뉴를 연다.
  onOpenAccount: (anchor: HTMLElement) => void;
  accountOpen?: boolean;
}

const navItems = [
  {
    to: appRoutes.terms,
    labelKey: "TermListTitle",
    icon: <AutoStoriesOutlined />,
    activeIcon: <AutoStoriesRounded />,
  },
  {
    to: appRoutes.learn,
    labelKey: "LearningTitle",
    icon: <StyleOutlined />,
    activeIcon: <StyleRounded />,
  },
];

/**
 * 로그인 후 메인 화면의 뼈대.
 * - 데스크톱(1200px~): 왼쪽 사이드바
 * - 태블릿(768px~): 아이콘만 있는 좁은 사이드 레일
 * - 모바일: 하단 탭바
 */
export default function AppShell({
  children,
  overlays,
  onOpenAccount,
  accountOpen = false,
}: AppShellProps) {
  const { t } = useTranslation();
  const profileImageSrc = useProfileImageSrc();
  const rankColor = useMyRankColor();
  const displayName = auth.currentUser?.displayName ?? "";
  const publicUrl = process.env.PUBLIC_URL;
  const avatar = (
    <img
      className={styles.avatar}
      src={profileImageSrc ?? `${publicUrl}/imgProfileUserDefaultr.png`}
      onError={(e) => {
        e.currentTarget.src = `${publicUrl}/imgProfileUserDefaultr.png`;
      }}
      alt=""
    />
  );

  return (
    <div className={styles.shell}>
      <aside className={styles.side}>
        <NavLink to={appRoutes.terms} className={styles.brand}>
          <img
            className={styles.brandLogo}
            src={`${publicUrl}/imgOnevocaLogoBlue.png`}
            alt="onevoca"
          />
          <img
            className={styles.brandMark}
            src={`${publicUrl}/icon-192.png`}
            alt="onevoca"
          />
        </NavLink>
        <nav className={styles.sideNav} aria-label="메인 메뉴">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end
              className={({ isActive }) =>
                classNames(styles.sideItem, { [styles.active]: isActive })
              }
            >
              {({ isActive }) => (
                <>
                  <span className={styles.navIcon}>
                    {isActive ? item.activeIcon : item.icon}
                  </span>
                  <span className={styles.sideLabel}>{t(item.labelKey)}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>
        <button
          type="button"
          className={classNames(styles.profile, {
            [styles.profileOpen]: accountOpen,
          })}
          aria-haspopup="dialog"
          aria-expanded={accountOpen}
          aria-label={t("PersonalInformation")}
          onClick={(e) => onOpenAccount(e.currentTarget)}
        >
          {avatar}
          <span className={styles.profileName} style={{ color: rankColor }}>
            {displayName}
          </span>
        </button>
      </aside>

      <main className={styles.main}>{children}</main>

      <nav className={styles.bottomNav} aria-label="메인 메뉴">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end
            className={({ isActive }) =>
              classNames(styles.bottomItem, { [styles.active]: isActive })
            }
          >
            {({ isActive }) => (
              <>
                <span className={styles.navIcon}>
                  {isActive ? item.activeIcon : item.icon}
                </span>
                {t(item.labelKey)}
              </>
            )}
          </NavLink>
        ))}
        <button
          type="button"
          className={classNames(styles.bottomItem, {
            [styles.active]: accountOpen,
          })}
          aria-haspopup="dialog"
          aria-expanded={accountOpen}
          onClick={(e) => onOpenAccount(e.currentTarget)}
        >
          <span className={styles.navIcon}>
            {accountOpen ? <PersonRounded /> : <PersonOutlineRounded />}
          </span>
          {t("PersonalInformation")}
        </button>
      </nav>

      {overlays && <div className={styles.overlays}>{overlays}</div>}
    </div>
  );
}
